import { EntityFilterConfig } from '../types/filter-config';
import { Entity, Path, PathOfType, QueryFilter } from '@softline/core';
import { FilterItemConfigOptions } from '../types/filter-item-config-options';
import { EntityFilterOperator } from '../types/filter-operators';
import { FilterStateFactoryRef } from './filter.function';
import { EntityFilter } from '../types/filter';
import { defaultFilterInputTypeFactory } from '../page-filter/helper/default-filter-input-type.factory';

export interface EntityFilterOptions<T extends object, TValue extends Entity> extends FilterItemConfigOptions<T, TValue> {
  additionalFields: Path<TValue>[];
  defaultOperator?: EntityFilterOperator;
  allowedOperators?: EntityFilterOperator[];
  sortField: Path<TValue>;
  idType?: 'string' | 'number'
  formatter: (value: TValue) => string;
  queryColumn?: Path<TValue>;
}

// Top-level keys of T that point to an Entity relation. Kept deliberately non-recursive:
// a recursive-conditional constraint (like PathOfType<T, Entity>) prevents TS from inferring
// K from the `field` literal, which collapses TValue to the full Entity union of T.
type EntityField<T extends object> = {
  [K in keyof T & string]: T[K] extends Entity ? K : never;
}[keyof T & string];

// TValue is derived from the `field` key (K) so callers don't have to specify it.
// K is the only inference site; TValue never appears in an inferable position on its own.
export function entityFilter<T extends object, K extends EntityField<T>>(
  ref: FilterStateFactoryRef<T>,
  options: Omit<EntityFilterOptions<T, T[K] & Entity>, 'field'> & { field: K }): void {
  const config = {
    ...options,
    type: 'entity',
    defaultOperator: options.defaultOperator ?? 'in',
    allowedOperators: options.allowedOperators ?? ['in', 'notIn', 'equals', 'notEquals', 'contains', 'empty', 'notEmpty'],
    toQueryFilter,
    inputTypeFactory: defaultFilterInputTypeFactory,
    queryColumn: options.queryColumn ?? 'id',
    idType: options.idType ?? 'number'
  } as unknown as EntityFilterConfig<T, Entity>;
  ref.addItem(config);
}


function toQueryFilter<T extends object, TValue extends Entity>(value: EntityFilter<T, TValue>): QueryFilter<T> {
  if(value.idType === 'string')
      return toStringIdQueryFilter(value as unknown as EntityFilter<T, { id: string }>);
  return toNumberIdQueryFilter(value as unknown as EntityFilter<T, { id: number }>);
}

function toStringIdQueryFilter<T extends object, TValue extends { id: string }>(value: EntityFilter<T, TValue>): QueryFilter<T> {
  switch (value.operator) {
    case 'empty':
      return { name: `${value.field}.id` as PathOfType<T, string>, type: 'stringEmpty' };
    case 'notEmpty':
      return { name: `${value.field}.id` as PathOfType<T, string>, type: 'stringNotEmpty' };
    case 'equals':
      return { name: `${value.field}.id` as PathOfType<T, string>, type: 'stringEquals', value: value.value.id };
    case 'notEquals':
      return { name: `${value.field}.id` as PathOfType<T, string>, type: 'stringNotEquals', value: value.value.id };
    case 'in':
      return { name: `${value.field}.id` as PathOfType<T, string>, type: 'stringSet', values: value.value.map(v => v.id) };
    case 'notIn':
      throw new Error('operator notIn not supported yet by the api');
  }
}

function toNumberIdQueryFilter<T extends object, TValue extends { id: number }>(value: EntityFilter<T, TValue>): QueryFilter<T> {
  switch (value.operator) {
    case 'empty':
      return { name: `${value.field}.id` as PathOfType<T, number>, type: 'numberEmpty' };
    case 'notEmpty':
      return { name: `${value.field}.id` as PathOfType<T, number>, type: 'numberNotEmpty' };
    case 'equals':
      return { name: `${value.field}.id` as PathOfType<T, number>, type: 'numberEquals', value: value.value.id };
    case 'notEquals':
      return { name: `${value.field}.id` as PathOfType<T, number>, type: 'numberNotEquals', value: value.value.id };
    case 'in':
      return { name: `${value.field}.id` as PathOfType<T, number>, type: 'numberSet', values: value.value.map(v => v.id) };
    case 'notIn':
      throw new Error('operator notIn not supported yet by the api');
  }
}
